import { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(() => {
        try {
            const raw = localStorage.getItem("kc_user");
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    });
    const [impersonator, setImpersonator] = useState(() => {
        try {
            const raw = localStorage.getItem("kc_impersonator");
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const token = localStorage.getItem("kc_token");
        if (!token) {
            setLoading(false);
            return;
        }
        // Try once; if the network / origin is flaky, retry once after
        // 1.5s before giving up. Only treat 401 (real auth failure) as
        // a sign that the token is invalid — 5xx/520/network errors
        // are transient and must NOT log the user out. Otherwise the
        // user gets kicked off whenever the proxy hiccups.
        const tryMe = (attempt = 0) =>
            api.get("/auth/me")
                .then((r) => {
                    setUser(r.data);
                    localStorage.setItem("kc_user", JSON.stringify(r.data));
                    setLoading(false);
                })
                .catch((err) => {
                    const status = err?.response?.status;
                    if (status === 401 || status === 403) {
                        localStorage.removeItem("kc_token");
                        localStorage.removeItem("kc_user");
                        setUser(null);
                        setLoading(false);
                        return;
                    }
                    // Transient (network drop, 5xx, 520 from the proxy)
                    // — retry once, then keep the existing cached user
                    // so the SPA can still render. The next API call
                    // will surface the real error if it persists.
                    if (attempt === 0) {
                        setTimeout(() => tryMe(1), 1500);
                        return;
                    }
                    setLoading(false);
                });
        tryMe();
    }, []);

    const login = (token, userData) => {
        localStorage.setItem("kc_token", token);
        localStorage.setItem("kc_user", JSON.stringify(userData));
        setUser(userData);
    };

    const startImpersonation = (targetToken, targetUser, impersonatorInfo) => {
        // Backup original session so we can "return" later.
        const originalToken = localStorage.getItem("kc_token");
        const originalUser = localStorage.getItem("kc_user");
        if (originalToken && originalUser) {
            localStorage.setItem("kc_token_original", originalToken);
            localStorage.setItem("kc_user_original", originalUser);
        }
        localStorage.setItem("kc_token", targetToken);
        localStorage.setItem("kc_user", JSON.stringify(targetUser));
        localStorage.setItem(
            "kc_impersonator",
            JSON.stringify(impersonatorInfo),
        );
        setUser(targetUser);
        setImpersonator(impersonatorInfo);
        // Drop any brand selection that might not apply to the new user.
        localStorage.removeItem("kc_current_brand_id");
        // No hard reload: a full-page navigation here used to fire ~17
        // simultaneous /api requests against a freshly-restarted set of
        // workers, which would queue + timeout + cascade into Cloudflare
        // 520s across the board. By staying inside the React tree we let
        // each page that mounts (Users → wherever the caller navigates)
        // request only what it needs, the auth + brand contexts re-fetch
        // because `user` changed, and the burst is spread over the
        // natural render cycle instead of a single tick.
    };

    const stopImpersonation = () => {
        const origToken = localStorage.getItem("kc_token_original");
        const origUser = localStorage.getItem("kc_user_original");
        if (!origToken || !origUser) {
            // fallback: go to login
            logout();
            return;
        }
        localStorage.setItem("kc_token", origToken);
        localStorage.setItem("kc_user", origUser);
        localStorage.removeItem("kc_token_original");
        localStorage.removeItem("kc_user_original");
        localStorage.removeItem("kc_impersonator");
        localStorage.removeItem("kc_current_brand_id");
        setImpersonator(null);
        setUser(JSON.parse(origUser));
        // Same rationale as startImpersonation: skip the hard reload.
        // ImpersonationBanner (the only caller today) will redirect via
        // react-router navigate after this returns.
    };

    const logout = () => {
        localStorage.removeItem("kc_token");
        localStorage.removeItem("kc_user");
        localStorage.removeItem("kc_token_original");
        localStorage.removeItem("kc_user_original");
        localStorage.removeItem("kc_impersonator");
        setUser(null);
        setImpersonator(null);
        window.location.href = "/login";
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                impersonator,
                login,
                logout,
                startImpersonation,
                stopImpersonation,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
